import React from 'react';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import { useDealerContext } from "../DealerContext.jsx";
import useApi from "../../../lib/apiService";
import { useFlags } from 'launchdarkly-react-client-sdk';
import './InventorySelection.css';
import InventorySourceDropdown from './InventorySourceDropdown'; // Import the new component

function InventorySelection(props) {
    const Api = useApi();
    const context = useDealerContext();
    const { pdtEnableAutoTrader, pdtEnableHomeNet } = useFlags();
    const ewsDealer = props.ewsDealer;
    const corgData = props.corgData;
    const cviConfigData = props.cviConfigData;
    const corgLoading = props.corgLoading;
    const [platformIdResults, setPlatformIdResults] = React.useState({});
    const [contactIdResults, setContactIdResults] = React.useState({});
    
    // Compute all unique platform IDs from all sources (EWS, CORG, and CVI config)
    const corgPlatformIds = React.useMemo(() => {
      return corgData?.platformIds || [];
    }, [corgData]);
    
    const ewsPlatformId = ewsDealer?.platformId;
    const cviConfigEntityId = cviConfigData?.entityId;
    
    // Get all unique platform IDs, preferring CORG sources
    const allPlatformIds = React.useMemo(() => {
      const uniqueIds = [...new Set([
        ...corgPlatformIds, 
        ...(ewsPlatformId ? [ewsPlatformId] : []),
        ...(cviConfigEntityId ? [cviConfigEntityId] : [])
      ])];
      return uniqueIds;
    }, [corgPlatformIds, ewsPlatformId, cviConfigEntityId]);
    
    // Initialize data when either ewsDealer or corgData changes
    React.useEffect(() => {
      if (allPlatformIds) {
        // Initialize platform IDs results
        let pidResults = {};
        for (let p in allPlatformIds) {
          pidResults[allPlatformIds[p]] = null;
        }
        setPlatformIdResults(pidResults);
      }
    }, [allPlatformIds]);
        
    React.useEffect(() => {
      if (corgData) {
        // Initialize HomeNet IDs results
        let iolResults = {};
        for (let p in corgData.homenetIds) {
          iolResults[corgData.homenetIds[p]] = null;
        }
        setContactIdResults(iolResults);
      }
    }, [corgData]);

    // Load vehicle counts for platform IDs
    React.useEffect(() => {
      async function loadVehiclesForEntity(entityId) {
        let vehicles = await Api.getCviVehiclesForEntity(entityId);
        let pr = { ...platformIdResults };
        pr[entityId] = vehicles ? vehicles.length : -1;
        setPlatformIdResults(pr);
      }

      function getKeyByValue(object, value) {
        return Object.keys(object).find(key => object[key] === value);
      }

      let eid = getKeyByValue(platformIdResults, null);
      if (eid) {
        loadVehiclesForEntity(eid);
      }
    }, [platformIdResults]);

    // Load virtual rooftops for HomeNet IDs
    React.useEffect(() => {
      if (pdtEnableHomeNet) {
        async function loadVirtualRooftopsForContact(contactId) {
          let vrt = await Api.getVirtualRooftopsForContact(contactId);
          if (vrt) {
            let x = { ...contactIdResults };
            x[contactId] = vrt;
            setContactIdResults(x);
          }
        }

        function getKeyByValue(object, value) {
          return Object.keys(object).find(key => object[key] === value);
        }

        let eid = getKeyByValue(contactIdResults, null);
        if (eid) {
          loadVirtualRooftopsForContact(eid);
        }
      }
    }, [contactIdResults, pdtEnableHomeNet]);

    // Helper to show vehicle count status for platform IDs
    const getPlatformIdDisplay = (platformId) => {
      let status = "";
      if (!platformIdResults[platformId] && platformIdResults[platformId] !== 0) {
        status = " (Loading...)";
      } else if (platformIdResults[platformId] === -1) {
        status = " (No data)";
      } else {
        status = ` (${platformIdResults[platformId]} vehicles)`;
      }
      
      const isFromEws = ewsPlatformId === platformId;
      const isFromCorg = corgPlatformIds.includes(platformId);
      const isFromCviConfig = cviConfigEntityId === platformId;
      let source = '';
      if (isFromCorg) {
        source = ' (CORG)';
      } else if (isFromEws) {
        source = ' (EWS)';
      } else if (isFromCviConfig) {
        source = ' (CVI)';
      }
      return `${platformId}${status}${source}`;
    };
  
    if (!ewsDealer && !corgData && !cviConfigData) {
      return <Spinner animation="border" size="sm" />;
    }
    
    // Show appropriate state when corgData is not available
    if (!corgData && !cviConfigData) {
      if (corgLoading) {
        // Show loading spinner when data is actually loading
        return (
          <div className="dealer-selections">
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading dealer data...</span>
            </div>
          </div>
        );
      } else if (ewsDealer && !ewsDealer.commonOrgId) {
        // Show message when dealer has no commonOrgId
        return (
          <div className="dealer-selections">
            <Alert variant="secondary">
              No Common Org data available. 
            </Alert>
          </div>
        );
      } else {
        // Show error message if not loading but data is still missing
        return (
          <div className="dealer-selections">
            <Alert variant="warning">
              Failed to load Common Org data.
            </Alert>
          </div>
        );
      }
    }
    
    const platformIdOptions = allPlatformIds.map(pid => ({
      key: pid,
      value: pid,
      display: getPlatformIdDisplay(pid),
    }));

    // CVI 2.1 reads its inventory records by Common Org id, unlike CVI 1.0's platform id.
    const cvi2Options = (corgData?.commonOrgId ? [corgData.commonOrgId] : []).map(id => ({
      key: id,
      value: id,
      display: id,
    }));

    const ddcOptions = (corgData?.ddcIds || []).map(id => ({
      key: id,
      value: id,
      display: id,
    }));

    const vinOptions = (corgData?.vinIds || []).map(id => ({
      key: id,
      value: id,
      display: id,
    }));

    const atcOptions = (corgData?.autotraderIds || []).map(id => ({
      key: id,
      value: id,
      display: id,
    }));
    
    const homenetIsLoading = corgData?.homenetIds && 
                             Object.values(contactIdResults).some(result => result === null);

    const homenetOptions = corgData?.homenetIds ?
      Object.entries(contactIdResults).flatMap(([contactId, vrts]) =>
        vrts ? vrts.map(vrt => ({
          key: String(vrt.virtualRooftopId), // Ensure key is a string
          value: String(vrt.virtualRooftopId), // Ensure value is a string for comparison
          display: `${vrt.name} (${vrt.virtualRooftopId})`,
        })) : []
      ) : [];

    return (
      <div className="dealer-selections">
        <Table size="sm" className="table table-borderless inventory-selection-table">
          <tbody>
            <InventorySourceDropdown
              id="platformId-dropdown"
              label="Platform"
              currentValue={context.state.entityId}
              setContextValue={context.setEntity}
              options={platformIdOptions}
              defaultOptionLabel="Select OrgModel ID"
              manualEntryPromptText="Enter OrgModel ID:"
            />
            
            <InventorySourceDropdown
              id="cavi2-dropdown"
              label="CVI 2.1"
              currentValue={context.state.cvi2DealerId}
              setContextValue={context.setCvi2}
              options={cvi2Options}
              defaultOptionLabel="Select Common Org ID"
              manualEntryPromptText="Enter Common Org ID:"
            />

            <InventorySourceDropdown
              id="ddc-dropdown"
              label="DDC"
              currentValue={context.state.ddcDealerId}
              setContextValue={context.setDdc}
              options={ddcOptions}
              defaultOptionLabel={`Select DDC Account ID`}
              manualEntryPromptText="Enter DDC Account ID:"
            />
            
            <InventorySourceDropdown
              id="vin-dropdown"
              label="VinSolutions"
              currentValue={context.state.vinDealerId}
              setContextValue={context.setVin}
              options={vinOptions}
              defaultOptionLabel={`Select VIN ID`}
              manualEntryPromptText="Enter VIN ID:"
            />
            {pdtEnableAutoTrader && 
            <InventorySourceDropdown
              id="atc-dropdown"
              label="AutoTrader"
              currentValue={context.state.atcDealerId}
              setContextValue={context.setAtc}
              options={atcOptions}
              defaultOptionLabel={`Select AutoTrader Owner ID`}
              manualEntryPromptText="Enter AutoTrader Owner ID:"
            />}
            {pdtEnableHomeNet &&
            <InventorySourceDropdown
              id="iol-dropdown"
              label="HomeNet"
              currentValue={context.state.iolVrtId}
              setContextValue={context.setIol}
              options={homenetOptions}
              defaultOptionLabel={`Select Virtual Rooftop`}
              manualEntryPromptText="Enter Virtual Rooftop ID:"
              isLoading={homenetIsLoading}
              loadingText="Loading VRTs..."
            />}
          </tbody>
        </Table>
        
        <div className="checkbox-row d-flex flex-row mt-1">
          <div className="me-4 checkbox-compact">
            <label id="include-sold-checkbox-label">
              <input 
                type="checkbox" 
                checked={context.state.includeSold} 
                onChange={() => {context.toggleSold()}} 
                id="include-sold-checkbox"
              /> Include Inactive/Sold Inventory
            </label>
          </div>
        </div>
      </div>
    );
}

export default InventorySelection;
